<template>
  <form class="core-form" :class="classes" @submit.prevent="handleSubmit">
    <FormFields
      class="core-form__form-fields"
      :fields="controller.visibleFields"
      :form-context="formContext"
      @input="handleFieldInput"
      @blur="handleFieldBlur"
      @focus="handleFieldFocus"
    >
      <template v-for="(_, slotName) in $scopedSlots" v-slot:[slotName]="slotProps">
        <slot
          :name="slotName"
          v-bind="slotProps"
          :input="value => handleFieldInput(slotProps.field, value)"
          :blur="() => handleFieldBlur(slotProps.field)"
          :focus="() => handleFieldFocus(slotProps.field)"
        />
      </template>
    </FormFields>

    <vue-recaptcha
      class="core-form__recaptcha"
      ref="recaptcha"
      size="invisible"
      :sitekey="$root.sitekeyRecaptcha"
      load-recaptcha-script
      @verify="handleRecaptchaVerify"
      @expired="handleRecaptchaExpired"
      @error="handleRecaptchaError"
    />

    <slot
      name="form-actions"
      :is-steps="controller.isStepMode"
      :is-first-step="controller.isFirstStep"
      :is-last-step="controller.isLastStep"
      :back="toBackStep"
      :next="toNextStep"
    >
      <FormActions
        :is-steps="controller.isStepMode"
        :is-first-step="controller.isFirstStep"
        :is-last-step="controller.isLastStep"
        :disabled="isSubmitDisabled"
        :submit-label="submitLabel"
        :next-label="nextLabel"
        :back-label="backLabel"
        @back="toBackStep"
        @next="toNextStep"
      />
    </slot>
  </form>
</template>

<script>
import { mapGetters } from 'vuex'
import VueRecaptcha from 'vue-recaptcha'
import { formController } from '@shared/services/form/form.controller'
import { FORM_EVENTS, FORM_STATUS } from '@shared/const/form/form.status'
import FormFields from '@shared/ui/public/forms/components/FormFields.vue'
import FormActions from '@shared/ui/public/forms/components/FormActions.vue'
import { DEFAULT_EXTRA_FIELD_NAMES } from '@shared/const/form/form.field'

export default {
  name: 'CoreForm',
  components: {
    VueRecaptcha,
    FormFields,
    FormActions,
  },
  props: {
    method: {
      type: String,
      default: 'post',
    },
    formConfig: {
      type: Object,
      required: true,
    },
    action: {
      type: String,
      required: true,
    },
    extraFieldNames: {
      type: Array,
      default: () => DEFAULT_EXTRA_FIELD_NAMES,
    },
    extraSubmitData: {
      type: Object,
      default: () => ({}),
    },
    beforeSubmitFn: {
      type: Function,
      default: $ => $,
    },
    agreements: {
      type: Array,
      default: () => [],
    },
    backLabel: {
      type: String,
      default: window.staticText.forms.back,
    },
    nextLabel: {
      type: String,
      default: window.staticText.forms.then,
    },
    submitLabel: {
      type: String,
      default: window.staticText.forms.regBtn,
    },
  },
  data() {
    return {
      controller: null,
    }
  },
  created() {
    this.controller = formController({
      method: this.method,
      formConfig: this.formConfig,
      formContext: this.formContext,
      formAgreements: this.agreements,
      action: this.action,
      extraSubmitData: this.extraSubmitData,
      beforeSubmitFn: this.beforeSubmitFn,
      extraFieldNames: this.extraFieldNames,

      onFormInit: ({ formState, publicApi }) => {
        this.$emit('form:init', { formState, publicApi })
      },
      onSubmitting: ({ recaptchaToken }) => {
        this.$emit('form:submitting', { recaptchaToken })
      },
      onSuccessSubmit: ({ formId, response }) => {
        this.$emit('form:submit-success', { formId, response })
      },
      onErrorSubmit: ({ formId }) => {
        this.$emit('form:submit-error', { formId })
      },
      onFieldError: ({ formId, field }) => {
        this.$emit('form:field-error', { formId, field })
      },
      onBackStep: ({ formId, currentStep }) => {
        this.$emit('form:back-step', { formId, currentStep })
      },
      onNextStep: ({ formId, currentStep, isValid }) => {
        this.$emit('form:next-step', { formId, currentStep, isValid })
      },
      onValidationError: ({ formId }) => {
        this.$emit('form:validation-error', { formId })
      },
    })

    this.controller.callEvent(FORM_EVENTS.INIT)
  },
  computed: {
    ...mapGetters('lk', ['isAuth']),
    ...mapGetters('locale', ['region']),

    formContext() {
      return {
        isAuth: this.isAuth,
        region: this.region,
        isRU: __AXOFT__.isRU,
        entityId: this.state?.form?.entityId,

        locale: this.$router.history.base !== '' ? this.$router.history.base.substring(1) : 'ru',
        location: this.state?.dadataLocation,
        maximum: this.state?.maximum,
      }
    },
    state() {
      return this.controller?.state || {}
    },
    classes() {
      if (!this.controller) return []

      return [
        `core-form--theme-${this.state.form.theme}`,
        {
          'core-form--submitting': this.state.status === FORM_STATUS.SUBMITTING,
        },
      ]
    },
    isSubmitDisabled() {
      return this.state.status === FORM_STATUS.SUBMITTING || this.state.status === FORM_STATUS.VALIDATION
    },
  },
  methods: {
    handleFieldInput(field, value) {
      this.controller.callEvent(FORM_EVENTS.FIELD_INPUT, { field, value })
    },
    handleFieldBlur(field) {
      this.controller.callEvent(FORM_EVENTS.FIELD_BLUR, { field })
    },
    handleFieldFocus(field) {
      this.controller.callEvent(FORM_EVENTS.FIELD_FOCUS, { field })
    },
    async handleSubmit() {
      if (!this.controller) return

      const isValid = await this.controller.callEvent(FORM_EVENTS.VALIDATION)

      if (!isValid) {
        return
      }

      if (!this.$refs.recaptcha) {
        console.error('Provide ref for recaptcha component')
        return
      }

      this.$refs.recaptcha.execute()
    },
    async handleRecaptchaVerify(recaptchaToken) {
      await this.controller.callEvent(FORM_EVENTS.SUBMIT, { recaptchaToken })

      if (this.$refs.recaptcha) {
        this.$refs.recaptcha.reset()
      }
    },
    handleRecaptchaExpired() {
      if (this.$refs.recaptcha) {
        this.$refs.recaptcha.reset()
        console.error('errorCaptcha: recaptcha was expired')
      }
    },
    handleRecaptchaError(error) {
      console.error('errorCaptcha:', error)
    },
    async toBackStep() {
      await this.controller.callEvent(FORM_EVENTS.BACK_STEP)
    },
    async toNextStep() {
      await this.controller.callEvent(FORM_EVENTS.NEXT_STEP)
    },
  },
}
</script>

<style lang="sass">
@import "~@src/legacy/sass/params"

.core-form
  --field-chip-bg-color: #{$cyan-light}
  --field-opacity-disabled: 0.7
  --field-error-border-color: #EF7171
  --field-error-border-width: 1px
  --field-border-width: 1px

  display: flex
  flex-direction: column
  gap: 24px

  &--theme-cyan
    --field-text-color: #{$white}
    --field-text-inverse-color: #{$black}
    --field-placeholder-color: rgba(255, 255, 255, .75)
    --field-border-color: #E6F7F1
    --field-border-focus-color: #{$white}
    --field-checkbox-border-color: #{$white}
    --field-icon-color: #{$white}
    --field-error-text-color: #{$white}
    --field-asterisk-color: #{$white}
    --field-disabled-bg-color: transparent
    --field-dropdown-hover: #00a2ae
    --form-action-text-hover-color: #{$cyan}
    --form-action-bg-color: #{$white}
    --form-action-bg-hover-color: #{$white}
    --form-link-color: rgba(255, 255, 255, .7)
    --form-link-hover: #{$white}

  &--theme-white
    --field-text-color: #{$black}
    --field-text-inverse-color: #{$white}
    --field-placeholder-color: #{$gray-text}
    --field-border-color: #C8C8C8
    --field-border-focus-color: #{$cyan}
    --field-checkbox-border-color: #C8C8C8
    --field-icon-color: #{$cyan}
    --field-error-text-color: #EF7171
    --field-asterisk-color: #EF7171
    --field-disabled-bg-color: #ededed
    --field-dropdown-hover: #{$cyan}
    --form-action-text-hover-color: #{$white}
    --form-action-bg-color: #{$cyan}
    --form-action-bg-hover-color: #{$cyan-light}
    --form-link-color: #{$cyan}
    --form-link-hover: #{$cyan-light}

  &--submitting
    pointer-events: none
    opacity: .7

  &__recaptcha
    position: fixed
</style>



// WEBPACK FOOTER //
// src/modules/shared/ui/public/forms/CoreForm.vue